import Logo from "shared/branding/logo.svg?url";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonVariant,
  Show
} from "shared/components";
import linesSvg from "../components/lines.svg";
import { cx } from "shared/utils";
import type { LandXSurvey } from "api/landlord-surveys/land-x.types";
import { useSearchParams } from "react-router-dom";
import { Banner } from "shared/components/Banner";
import { AlertCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";

// Uses `compact` prop instead of `md:` breakpoints because this component is
// reused inside preview containers where the viewport width doesn't reflect
// the actual available space (e.g. mobile preview within a wide desktop viewport).
export const StartSurveySlice = ({
  survey,
  onContinue,
  onDecline,
  isDeclining,
  compact,
  // Mostly used for previews
  mockLandlordInfo
}: {
  survey?: LandXSurvey | null;
  onContinue?: () => void;
  onDecline?: () => void;
  isDeclining?: boolean;
  compact?: boolean;
  mockLandlordInfo?: {
    name: string;
    address: string;
  };
}) => {
  const desktop = !compact;
  const landlord = survey?.landlord_details;
  const address = survey?.address;

  const [searchParams] = useSearchParams();
  const asPartnerUser = searchParams.get("as_partner_user") === "true";

  return (
    <div
      className={cx(
        "flex relative h-full",
        desktop ? "p-6 gap-6 flex-row" : "pt-16 flex-col-reverse"
      )}
    >
      <a
        href="https://www.boompay.app/boomscreen"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          src={Logo}
          alt="Boom"
          className={cx(
            "absolute",
            desktop ? "top-6 left-6 h-8" : "top-4 left-4 h-[30px]"
          )}
        />
      </a>
      <main
        className={cx(
          "flex flex-col justify-center w-full",
          desktop ? "p-0" : "px-4 pb-4 pt-6 shrink-0"
        )}
      >
        <div className="max-w-[400px] mx-auto">
          <h1
            className={cx("font-bold mb-4", desktop ? "text-2xl" : "text-xl")}
          >
            Help verify {survey?.applicant_name ?? "the tenant"}&apos;s rental
            history for their next landlord
          </h1>

          <Badge
            className="mb-4"
            variant={BadgeVariant.BASE}
            size={BadgeSize.SM}
          >
            Completion time: ~2 mins
          </Badge>

          <div className={cx("space-y-1 mb-4", desktop ? "space-y-2" : "mb-4")}>
            <p className={desktop ? "text-sm" : "text-xs"}>
              <span className="text-content-300">Landlord:</span>{" "}
              <span className="font-medium text-content-1000">
                {mockLandlordInfo?.name || (landlord?.name ?? "—")}
              </span>
            </p>
            <p className="text-sm">
              <span className="text-content-300">Property address:</span>{" "}
              <span className="font-medium text-content-1000">
                {mockLandlordInfo?.address || (address?.address ?? "—")}
              </span>
            </p>
          </div>

          <Show if={asPartnerUser}>
            <Banner
              HugeIcon={
                <HugeiconsIcon
                  className="text-primary-500"
                  strokeWidth={1.5}
                  icon={AlertCircleIcon}
                />
              }
              className="-mx-4 mb-6"
              variant="base"
              borders="top"
              body={`You are filling out the survey on behalf of the landlord (${landlord?.name} ${landlord?.email})`}
            />
          </Show>
          <Button
            variant={ButtonVariant.PRIMARY}
            className={cx("w-full", desktop ? "mb-3" : "mb-2")}
            onClick={onContinue}
          >
            Continue
          </Button>

          <Button
            className="mb-2 w-full"
            onClick={onDecline}
            loading={isDeclining}
          >
            Decline survey
          </Button>

          <p
            className={cx(
              "font-medium text-content-700",
              desktop ? "text-sm" : "text-xs"
            )}
          >
            View a{" "}
            <a
              href="https://www.boompay.app/legal"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-500"
            >
              submission disclosure
            </a>{" "}
            provided by the applicant
          </p>
        </div>
      </main>

      <aside
        className={cx(
          "relative bg-primary-500 text-white flex flex-col w-full",
          desktop
            ? "max-w-[540px] p-10 h-full rounded-xl"
            : "px-4 py-6 flex-grow"
        )}
      >
        <img className="absolute right-0 select-none" src={linesSvg} alt="" />

        <h1
          className={cx(
            "font-bold mb-1 mt-auto",
            desktop ? "text-2xl" : "text-base"
          )}
        >
          Powered by BoomScreen
        </h1>
        <p className={desktop ? "text-base" : "text-xs"}>
          Smarter tenant underwriting
        </p>
      </aside>
    </div>
  );
};
